/**
 * O vocabulário de ESTADO de um ativo — um só, para todas as telas.
 *
 * Mora aqui e não na planta porque três telas falam esta língua: o marcador
 * na planta, os tiles do painel de parede e a legenda das duas. Duas cópias
 * divergem no dia em que alguém corrige uma — e o sintoma seria o pior
 * possível: a mesma geladeira verde numa tela e cinza na outra.
 *
 * CADA ESTADO CARREGA COR *E* FORMA. Cor sozinha não sobrevive a daltonismo,
 * à impressão em preto e branco, nem ao marcador pequeno visto de longe — e
 * de longe é justamente como esta informação é lida numa parede.
 *
 * As cores vêm de variáveis CSS (ver `index.css`) porque "sem comunicação"
 * precisa de um passo por tema pra não se confundir com "dentro do previsto"
 * — a confusão mais cara que esta tela pode produzir.
 */

import { AlertOctagon, AlertTriangle, CheckCircle2, WifiOff } from "lucide-react";

import type { ActiveAlert } from "@/types";

export type Estado = "ok" | "atencao" | "critico" | "mudo";

export const ESTADO = {
  critico: { rotulo: "Crítico",            cor: "var(--ep-estado-critico)", Icone: AlertOctagon },
  atencao: { rotulo: "Atenção",            cor: "var(--ep-estado-atencao)", Icone: AlertTriangle },
  mudo:    { rotulo: "Sem comunicação",    cor: "var(--ep-estado-mudo)",    Icone: WifiOff },
  ok:      { rotulo: "Dentro do previsto", cor: "var(--ep-estado-ok)",      Icone: CheckCircle2 },
} as const;

/** Ordem de gravidade — pro pior estado de um conjunto vencer. */
export const ORDEM_DOS_ESTADOS: Estado[] = ["critico", "atencao", "mudo", "ok"];

/** Regras de silêncio vencem severidade: sensor mudo não tem leitura, então
 *  "dentro da faixa" seria uma afirmação sobre um dado que não existe. */
export const KINDS_DE_SILENCIO = new Set(["device_offline", "tenant_silent"]);

export function estadoDoAtivo(alertas: ActiveAlert[]): Estado {
  if (!alertas.length) return "ok";
  if (alertas.some((a) => KINDS_DE_SILENCIO.has(a.rule_kind))) return "mudo";
  if (alertas.some((a) => a.severity === "critical" || a.severity === "disaster"))
    return "critico";
  return "atencao";
}

/** O pior estado de um conjunto — é o que um card de unidade anuncia. */
export function piorEstado(estados: Estado[]): Estado {
  for (const e of ORDEM_DOS_ESTADOS) if (estados.includes(e)) return e;
  return "ok";
}
